import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary } from '../styles/theme.js'
import { predictions as predictionsApi, tags as tagsApi, sessions as sessionsApi, projects as projectsApi } from '../lib/api.js'
import { realtime } from '../lib/realtime.js'

// --- Styles ---

const pageWrap = css`
  max-width: 900px;
  margin: 0 auto;
  padding: ${space.lg};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.xl};
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const formRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  margin-bottom: ${space.md};
`

const formLabel = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  min-width: 100px;
  flex-shrink: 0;
`

const selectStyle = css`
  padding: 6px 10px;
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-family: ${font.family};
  font-size: ${font.size.sm};
  background: ${colors.bgCard};
  color: ${colors.text};
`

const tagChip = css`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
  border: 2px solid transparent;
  transition: opacity 0.1s;
  &:hover { opacity: 0.85; }
`

const tagChipSelected = css`
  border-color: rgba(0,0,0,0.2) !important;
`

const tagGrid = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.xs};
  margin-bottom: ${space.md};
`

const progressBar = css`
  width: 100%;
  height: 8px;
  background: ${colors.border};
  border-radius: 4px;
  overflow: hidden;
  margin: ${space.sm} 0;
`

const progressFill = css`
  height: 100%;
  background: ${colors.brand};
  border-radius: 4px;
  transition: width 0.3s ease;
`

const jobRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm};
  &:last-child { border-bottom: none; }
`

const jobMeta = css`
  flex: 1;
  min-width: 0;
`

const jobStat = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const statusBadge = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
`

const noConfig = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  padding: ${space.lg};
  text-align: center;
`

// --- Session filter styles ---

const pfTagFilterContainer = css`
  position: relative;
`

const pfTagFilterBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${colors.bg}; }
`

const pfTagDropdown = css`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 280px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 50;
  overflow: hidden;
`

const pfTagSearchInput = css`
  width: 100%;
  padding: ${space.sm} ${space.md};
  border: none;
  border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: transparent;
  color: ${colors.text};
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${colors.textMuted}; }
`

const pfTagDropdownList = css`
  max-height: 240px;
  overflow-y: auto;
`

const pfTagDropdownItem = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: 6px ${space.md};
  cursor: pointer;
  font-size: ${font.size.sm};
  transition: background 0.08s;
  &:hover { background: ${colors.bg}; }
`

const pfTagDropdownSwatch = css`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`

const pfTagDropdownName = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const pfTagStateIndicator = css`
  font-size: 11px;
  font-weight: ${font.weight.semibold};
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const pfSelectedTagChips = css`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: ${space.sm};
`

const pfSelectedTagChip = css`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
  &:hover { opacity: 0.8; }
`

const pfTagModeToggle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  &:hover { color: ${colors.text}; }
`

const pfActiveFilterCount = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${colors.brand};
  color: white;
  font-size: 10px;
  font-weight: ${font.weight.semibold};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  margin-left: 4px;
`

const pfToggleFiltersBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  &:hover { background: ${colors.bg}; }
`

const pfFilterPanelStyle = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} 0;
  flex-shrink: 0;
  flex-wrap: wrap;
  margin-bottom: ${space.sm};
`

const pfFilterLabel = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.medium};
`

const pfFilterInput = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  width: 130px;
  &:focus { border-color: ${colors.borderFocus}; outline: none; }
`

const pfFilterDivider = css`
  width: 1px;
  height: 20px;
  background: ${colors.border};
  margin: 0 ${space.xs};
`

const pfClearFiltersBtn = css`
  padding: 3px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  &:hover { color: ${colors.text}; background: ${colors.bg}; }
`

const subsectionTitle = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  color: ${colors.text};
  margin-bottom: 4px;
`

const subsectionHint = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-bottom: ${space.sm};
`

// --- Component ---

export function PredictionsView() {
  let projectId = null
  let project = null
  let allTags = []
  let jobs = []
  let loading = true
  let running = false
  let error = null

  // Launch form state
  let granularity = 'session'
  let selectedTagIds = new Set()
  let filterSearch = ''
  let filterUntagged = false
  let hasConfig = false

  // Session filter state
  let filterIncludeTagIds = new Set()
  let filterExcludeTagIds = new Set()
  let filterTagMode = 'any'
  let filterTagDropdownOpen = false
  let filterTagSearch = ''
  let showAdvancedFilters = false
  let filterDateFrom = ''
  let filterDateTo = ''
  let filterMinMessages = ''
  let filterMaxMessages = ''

  // Live progress — Map of jobId → { jobId, completed, total, tagsApplied, errors }
  let liveProgress = new Map()
  let unsubProgress = null
  let unsubComplete = null

  async function loadData() {
    loading = true
    try {
      const [config, tagList, jobList] = await Promise.all([
        predictionsApi.getConfig(projectId),
        tagsApi.list(projectId),
        predictionsApi.listJobs(projectId),
      ])
      hasConfig = config && config.configured !== false
      allTags = tagList || []
      jobs = jobList || []
      // Seed progress for any currently running jobs
      for (const job of jobs) {
        if (job.status === 'running') {
          liveProgress.set(job.id, {
            jobId: job.id, completed: job.completed_count, total: job.session_count,
            tagsApplied: job.tags_applied, errors: job.error_count,
          })
        }
      }
    } catch (err) {
      console.error('Failed to load predictions data:', err)
      error = err.message
    } finally {
      loading = false
      m.redraw()
    }
  }

  function toggleTag(tagId) {
    if (selectedTagIds.has(tagId)) {
      selectedTagIds.delete(tagId)
    } else {
      selectedTagIds.add(tagId)
    }
  }

  // --- Session filter helpers ---

  function cycleFilterTag(tagId) {
    if (filterIncludeTagIds.has(tagId)) {
      filterIncludeTagIds.delete(tagId)
      filterExcludeTagIds.add(tagId)
    } else if (filterExcludeTagIds.has(tagId)) {
      filterExcludeTagIds.delete(tagId)
    } else {
      filterIncludeTagIds.add(tagId)
    }
    filterUntagged = false
  }

  function removeFilterTag(tagId) {
    filterIncludeTagIds.delete(tagId)
    filterExcludeTagIds.delete(tagId)
  }

  function getFilterTagState(tagId) {
    if (filterIncludeTagIds.has(tagId)) return 'include'
    if (filterExcludeTagIds.has(tagId)) return 'exclude'
    return 'off'
  }

  function filteredFilterTags() {
    if (!filterTagSearch.trim()) return allTags
    const q = filterTagSearch.toLowerCase()
    return allTags.filter(t => t.name.toLowerCase().includes(q))
  }

  function selectedFilterTags() {
    return allTags.filter(t => filterIncludeTagIds.has(t.id) || filterExcludeTagIds.has(t.id))
  }

  function activeFilterCount() {
    let count = 0
    if (filterSearch) count++
    if (filterUntagged) count++
    if (filterIncludeTagIds.size > 0) count++
    if (filterExcludeTagIds.size > 0) count++
    if (filterDateFrom) count++
    if (filterDateTo) count++
    if (filterMinMessages !== '') count++
    if (filterMaxMessages !== '') count++
    return count
  }

  function clearAllSessionFilters() {
    filterSearch = ''
    filterUntagged = false
    filterIncludeTagIds = new Set()
    filterExcludeTagIds = new Set()
    filterTagMode = 'any'
    filterDateFrom = ''
    filterDateTo = ''
    filterMinMessages = ''
    filterMaxMessages = ''
  }

  function onDocClick() {
    if (filterTagDropdownOpen) {
      filterTagDropdownOpen = false
      m.redraw()
    }
  }

  // --- Render helpers ---

  function renderFilterTagDropdown() {
    const selected = selectedFilterTags()
    const hasTagFilters = selected.length > 0

    return m('div', { class: pfTagFilterContainer }, [
      m('button', {
        class: pfTagFilterBtn,
        style: hasTagFilters ? `border-color: ${colors.brand};` : '',
        onclick: (e) => {
          e.stopPropagation()
          filterTagDropdownOpen = !filterTagDropdownOpen
          filterTagSearch = ''
        },
      }, [
        'Tags',
        hasTagFilters
          ? m('span', { class: pfActiveFilterCount }, selected.length)
          : null,
      ]),

      filterTagDropdownOpen
        ? m('div', {
            class: pfTagDropdown,
            onclick: (e) => e.stopPropagation(),
          }, [
            m('input', {
              class: pfTagSearchInput,
              type: 'text',
              placeholder: 'Search tags...',
              value: filterTagSearch,
              oninput: (e) => { filterTagSearch = e.target.value },
              oncreate: (vnode) => { vnode.dom.focus() },
              onkeydown: (e) => {
                if (e.key === 'Escape') {
                  filterTagDropdownOpen = false
                  e.stopPropagation()
                }
              },
            }),
            m('div', { class: pfTagDropdownList }, [
              m('div', {
                key: 'untagged',
                class: pfTagDropdownItem,
                onclick: () => {
                  filterUntagged = !filterUntagged
                  if (filterUntagged) {
                    filterIncludeTagIds = new Set()
                    filterExcludeTagIds = new Set()
                  }
                },
              }, [
                m('div', {
                  class: pfTagDropdownSwatch,
                  style: `background: ${colors.textMuted};`,
                }),
                m('span', { class: pfTagDropdownName }, 'Untagged'),
                filterUntagged
                  ? m('span', {
                      class: pfTagStateIndicator,
                      style: `background: ${colors.brand}; color: white;`,
                    }, '\u2713')
                  : null,
              ]),
              ...filteredFilterTags().map(t => {
                const state = getFilterTagState(t.id)
                return m('div', {
                  key: t.id,
                  class: pfTagDropdownItem,
                  onclick: () => cycleFilterTag(t.id),
                }, [
                  m('div', {
                    class: pfTagDropdownSwatch,
                    style: `background: ${t.color || colors.border};`,
                  }),
                  m('span', { class: pfTagDropdownName }, t.name),
                  t.shortcut
                    ? m('span', {
                        style: `font-size: 10px; color: ${colors.textMuted}; font-family: 'SF Mono', monospace;`,
                      }, t.shortcut.toUpperCase())
                    : null,
                  state === 'include'
                    ? m('span', {
                        class: pfTagStateIndicator,
                        style: `background: #a9e34b30; color: #5a8a00;`,
                        title: 'Including - click to exclude',
                      }, '+')
                    : state === 'exclude'
                      ? m('span', {
                          class: pfTagStateIndicator,
                          style: `background: ${colors.critical}20; color: ${colors.critical};`,
                          title: 'Excluding - click to clear',
                        }, '\u2212')
                      : null,
                ])
              }),
            ]),
          ])
        : null,
    ])
  }

  function renderFilterSelectedChips() {
    const selected = selectedFilterTags()
    if (selected.length === 0 && !filterUntagged) return null

    const chips = []
    if (filterUntagged) {
      chips.push(m('span', {
        key: 'untagged',
        class: pfSelectedTagChip,
        style: `background: ${colors.textMuted}30; color: ${colors.text}; border: 1px solid ${colors.textMuted};`,
        onclick: () => { filterUntagged = false },
        title: 'Click to remove',
      }, [
        'Untagged',
        m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7'),
      ]))
    }
    for (const t of selected) {
      const state = getFilterTagState(t.id)
      const isExclude = state === 'exclude'
      chips.push(m('span', {
        key: t.id,
        class: pfSelectedTagChip,
        style: isExclude
          ? `background: ${colors.critical}15; color: ${colors.critical}; border: 1px solid ${colors.critical}40; text-decoration: line-through;`
          : `background: ${t.color || colors.border}30; color: ${colors.text}; border: 1px solid ${t.color || colors.border};`,
        onclick: () => removeFilterTag(t.id),
        title: isExclude ? 'Excluding - click to remove' : 'Including - click to remove',
      }, [
        isExclude ? '\u2212 ' : '',
        t.name,
        m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7'),
      ]))
    }
    if (filterIncludeTagIds.size > 1) {
      chips.push(m('button', {
        key: 'mode-toggle',
        class: pfTagModeToggle,
        title: filterTagMode === 'any' ? 'Showing sessions with ANY included tag' : 'Showing sessions with ALL included tags',
        onclick: () => { filterTagMode = filterTagMode === 'any' ? 'all' : 'any' },
      }, filterTagMode === 'any' ? 'Any' : 'All'))
    }
    return m('div', { class: pfSelectedTagChips }, chips)
  }

  async function runPredictions() {
    running = true
    error = null
    m.redraw()
    try {
      const data = { granularity }
      if (selectedTagIds.size > 0) data.tagIds = [...selectedTagIds]
      const filter = {}
      if (filterSearch) filter.search = filterSearch
      if (filterUntagged) filter.untagged = true
      if (filterIncludeTagIds.size > 0) {
        filter.tagIds = [...filterIncludeTagIds]
        filter.tagMode = filterTagMode
      }
      if (filterExcludeTagIds.size > 0) {
        filter.excludeTagIds = [...filterExcludeTagIds]
      }
      if (filterDateFrom) filter.dateFrom = filterDateFrom
      if (filterDateTo) filter.dateTo = filterDateTo + 'T23:59:59'
      if (filterMinMessages !== '' && !isNaN(parseInt(filterMinMessages, 10))) {
        filter.minMessages = parseInt(filterMinMessages, 10)
      }
      if (filterMaxMessages !== '' && !isNaN(parseInt(filterMaxMessages, 10))) {
        filter.maxMessages = parseInt(filterMaxMessages, 10)
      }
      if (Object.keys(filter).length > 0) data.filter = filter
      const result = await predictionsApi.run(projectId, data)
      liveProgress.set(result.jobId, {
        jobId: result.jobId,
        total: result.sessionCount,
        completed: 0,
        tagsApplied: 0,
        errors: 0,
      })
      // Refresh job list
      jobs = await predictionsApi.listJobs(projectId)
    } catch (err) {
      error = err.message || 'Failed to start prediction run'
    } finally {
      running = false
      m.redraw()
    }
  }

  let cancellingJobs = new Set()
  async function cancelJob(jobId) {
    if (cancellingJobs.has(jobId)) return
    cancellingJobs.add(jobId)
    m.redraw()
    try {
      await predictionsApi.cancel(projectId, jobId)
    } catch (err) {
      console.error('Failed to cancel job:', err)
    }
    cancellingJobs.delete(jobId)
    m.redraw()
  }

  async function undoJob(jobId) {
    try {
      await predictionsApi.undo(projectId, jobId)
      jobs = await predictionsApi.listJobs(projectId)
    } catch (err) {
      console.error('Failed to undo job:', err)
    }
    m.redraw()
  }

  function statusColor(status) {
    if (status === 'completed') return colors.green
    if (status === 'running') return colors.brand
    if (status === 'failed') return colors.critical
    return colors.textMuted
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadData()
      realtime.connect(projectId)
      unsubProgress = realtime.on('prediction_progress', (data) => {
        liveProgress.set(data.jobId, data)
        m.redraw()
      })
      unsubComplete = realtime.on('prediction_complete', async (data) => {
        liveProgress.delete(data.jobId)
        jobs = await predictionsApi.listJobs(projectId)
        m.redraw()
      })
    },

    oncreate() {
      document.addEventListener('click', onDocClick)
    },

    onremove() {
      if (unsubProgress) unsubProgress()
      if (unsubComplete) unsubComplete()
      realtime.disconnect()
      document.removeEventListener('click', onDocClick)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      if (loading) return m('div', { class: pageWrap }, 'Loading...')

      const filterCount = activeFilterCount()

      return m('div', { class: pageWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / Tag Predictions',
        ]),

        m('h1', { class: pageTitle }, 'Tag Predictions'),

        !hasConfig
          ? m('div', { class: `${card} ${noConfig}` }, [
              'Prediction LLM not configured. ',
              m(m.route.Link, { href: `/projects/${projectId}/settings` }, 'Configure in Settings'),
              '.',
            ])
          : null,

        // Launch section
        hasConfig ? m('div', { style: `margin-bottom: ${space.xl};` }, [
          m('h2', { class: sectionTitle }, 'Run Predictions'),
          m('div', { class: card, style: `padding: ${space.lg};` }, [
            m('div', { class: formRow }, [
              m('span', { class: formLabel }, 'Granularity'),
              m('select', {
                class: selectStyle,
                value: granularity,
                onchange: (e) => { granularity = e.target.value },
              }, [
                m('option', { value: 'session' }, 'Per session'),
                m('option', { value: 'message' }, 'Per message'),
                m('option', { value: 'author' }, 'Per author role'),
              ]),
            ]),

            // --- Session Filters ---
            m('div', { class: subsectionTitle }, 'Session Filters'),
            m('div', { class: subsectionHint }, 'Which sessions to process'),

            // Filter toolbar: tag dropdown + filters toggle
            m('div', { style: `display: flex; align-items: center; gap: ${space.sm}; margin-bottom: ${space.sm};` }, [
              allTags.length > 0 ? renderFilterTagDropdown() : null,
              m('button', {
                class: pfToggleFiltersBtn,
                onclick: () => { showAdvancedFilters = !showAdvancedFilters },
              }, [
                'Filters',
                filterCount > 0
                  ? m('span', { class: pfActiveFilterCount }, filterCount)
                  : null,
              ]),
              filterCount > 0
                ? m('button', {
                    class: pfClearFiltersBtn,
                    onclick: clearAllSessionFilters,
                  }, 'Clear all')
                : null,
            ]),

            // Selected tag chips
            renderFilterSelectedChips(),

            // Advanced filter panel (collapsible)
            showAdvancedFilters
              ? m('div', { class: pfFilterPanelStyle }, [
                  m('span', { class: pfFilterLabel }, 'Search:'),
                  m('input', {
                    class: pfFilterInput,
                    type: 'text',
                    placeholder: 'Text search...',
                    value: filterSearch,
                    oninput: (e) => { filterSearch = e.target.value },
                  }),
                  m('span', { class: pfFilterDivider }),
                  m('span', { class: pfFilterLabel }, 'Date:'),
                  m('input', {
                    class: pfFilterInput,
                    type: 'date',
                    value: filterDateFrom,
                    onchange: (e) => { filterDateFrom = e.target.value },
                    title: 'From date',
                  }),
                  m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, 'to'),
                  m('input', {
                    class: pfFilterInput,
                    type: 'date',
                    value: filterDateTo,
                    onchange: (e) => { filterDateTo = e.target.value },
                    title: 'To date',
                  }),
                  m('span', { class: pfFilterDivider }),
                  m('span', { class: pfFilterLabel }, 'Messages:'),
                  m('input', {
                    class: pfFilterInput,
                    style: 'width: 70px;',
                    type: 'number',
                    placeholder: 'Min',
                    min: 0,
                    value: filterMinMessages,
                    onchange: (e) => { filterMinMessages = e.target.value },
                  }),
                  m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, '\u2013'),
                  m('input', {
                    class: pfFilterInput,
                    style: 'width: 70px;',
                    type: 'number',
                    placeholder: 'Max',
                    min: 0,
                    value: filterMaxMessages,
                    onchange: (e) => { filterMaxMessages = e.target.value },
                  }),
                  m('span', { class: pfFilterDivider }),
                  m('label', {
                    style: `display: inline-flex; align-items: center; gap: 4px; font-size: ${font.size.xs}; color: ${colors.textMuted}; cursor: pointer;`,
                  }, [
                    m('input', {
                      type: 'checkbox',
                      checked: filterUntagged,
                      onchange: (e) => {
                        filterUntagged = e.target.checked
                        if (filterUntagged) {
                          filterIncludeTagIds = new Set()
                          filterExcludeTagIds = new Set()
                        }
                      },
                    }),
                    'Untagged only',
                  ]),
                ])
              : null,

            // --- Prediction Tags ---
            allTags.length > 0
              ? m('div', { style: `margin-top: ${space.md};` }, [
                  m('div', { class: subsectionTitle },
                    selectedTagIds.size > 0 ? `Prediction Tags (${selectedTagIds.size} selected)` : 'Prediction Tags (all)'),
                  m('div', { class: subsectionHint }, 'Which tags the AI can apply'),
                  m('div', { class: tagGrid },
                    allTags.map(tag =>
                      m('span', {
                        key: tag.id,
                        class: `${tagChip} ${selectedTagIds.has(tag.id) ? tagChipSelected : ''}`,
                        style: `background: ${selectedTagIds.has(tag.id) ? (tag.color || colors.border) : colors.border}; color: ${colors.text};`,
                        onclick: () => toggleTag(tag.id),
                      }, tag.name)
                    )
                  ),
                ])
              : null,

            error ? m('div', {
              style: `font-size: ${font.size.sm}; color: ${colors.critical}; margin-bottom: ${space.sm};`,
            }, error) : null,

            m('button', {
              class: btnPrimary,
              disabled: running,
              onclick: runPredictions,
            }, running ? 'Starting...' : 'Run Predictions'),
          ]),
        ]) : null,

        // Live progress
        liveProgress.size > 0 ? m('div', { style: `margin-bottom: ${space.xl};` }, [
          m('h2', { key: 'progress-heading', class: sectionTitle }, liveProgress.size === 1 ? 'Progress' : `Progress (${liveProgress.size} jobs)`),
          ...[...liveProgress.values()].map(prog =>
            m('div', { key: prog.jobId, class: card, style: `padding: ${space.lg}; margin-bottom: ${space.sm};` }, [
              m('div', { style: `display: flex; justify-content: space-between; align-items: center; margin-bottom: ${space.xs};` }, [
                m('div', { style: `font-size: ${font.size.sm};` },
                  `${prog.completed} / ${prog.total} sessions processed`),
                m('button', {
                  class: btnSecondary,
                  style: `color: ${colors.critical}; border-color: ${colors.critical}; padding: ${space.xs} ${space.sm}; font-size: ${font.size.xs};`,
                  disabled: cancellingJobs.has(prog.jobId),
                  onclick: () => cancelJob(prog.jobId),
                }, cancellingJobs.has(prog.jobId) ? 'Stopping...' : 'Stop'),
              ]),
              m('div', { class: progressBar },
                m('div', {
                  class: progressFill,
                  style: `width: ${prog.total > 0 ? (prog.completed / prog.total * 100) : 0}%;`,
                })
              ),
              m('div', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted};` }, [
                `${prog.tagsApplied} tags applied`,
                prog.errors > 0 ? ` \u00b7 ${prog.errors} errors` : '',
              ]),
            ])
          ),
        ]) : null,

        // Job history
        m('div', [
          m('h2', { class: sectionTitle }, 'Job History'),
          jobs.length > 0
            ? m('div', { class: card },
                jobs.map(job => {
                  const config = (() => { try { return JSON.parse(job.config) } catch { return {} } })()
                  return m('div', { key: job.id, class: jobRow }, [
                    m('div', { class: jobMeta }, [
                      m('div', { style: `font-weight: ${font.weight.medium};` },
                        new Date(job.created_at).toLocaleString()),
                      m('div', { class: jobStat }, [
                        `${job.completed_count}/${job.session_count} sessions`,
                        ` \u00b7 ${job.tags_applied} tags`,
                        job.token_count ? ` \u00b7 ${job.token_count} tokens` : '',
                        job.error_count > 0 ? ` \u00b7 ${job.error_count} errors` : '',
                        config.granularity ? ` \u00b7 ${config.granularity}` : '',
                      ]),
                    ]),
                    m('span', {
                      class: statusBadge,
                      style: `color: ${statusColor(job.status)}; background: ${statusColor(job.status)}15;`,
                    }, job.status),
                    (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled')
                      ? m('button', {
                          class: btnSecondary,
                          style: `font-size: ${font.size.xs}; padding: 3px 8px;`,
                          onclick: () => undoJob(job.id),
                        }, 'Undo')
                      : null,
                  ])
                })
              )
            : m('div', {
                style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; padding: ${space.md};`,
              }, 'No prediction jobs yet.'),
        ]),
      ])
    },
  }
}
